import axios, { AxiosInstance, AxiosResponse } from 'axios';

import Auth from '@utils/helpers/auth';
import { CURRENT_ENV } from '@core/configs/env';
import { RcFile } from 'antd/lib/upload';
import { message } from 'antd';
import {
  IFilter,
  IQueryParams
} from '@root/modules/Sellers/Product/interfaces';
import { EventSourcePolyfill } from 'event-source-polyfill';

interface Axios extends AxiosInstance {
  [key: string]: any;
}

let isRefreshing = false;
let subscribers: string[] = [];

const onRefreshed = (authorisationToken: string) => {
  subscribers.map((cb: any) => cb(authorisationToken));
};

const subscribeTokenRefresh = (cb: any) => {
  subscribers.push(cb);
};

const axiosBase: Axios = axios.create({ baseURL: CURRENT_ENV.API_URL });
axiosBase.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const lang = Auth.getLanguage() === 'ko' ? 'kr' : 'en';

    if (token) {
      config.headers['Authorization'] = token;
      config.headers['Accept-Language'] = lang;
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

axiosBase.interceptors.response.use(
  (config) => config,
  (error) => {
    console.log(error);

    const { response } = error;
    if (response?.data.message) {
      // message.error(response.data.message);
    }
    return Promise.reject(error);
  }
);
// const listStatus = [
//   500,
//   404,
//   400,
// ];
const authAxios: Axios = axios.create({ baseURL: CURRENT_ENV.API_URL });
authAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const lang = Auth.getLanguage() === 'ko' ? 'kr' : 'en';

    if (token) {
      config.headers['Authorization'] = token;
      config.headers['Accept-Language'] = lang;
      config.headers['Accept-Content'] = 'application/json';
      config.headers['Accept'] = '*/*';
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

authAxios.interceptors.response.use(
  (config) => config,
  (error) => {
    const { response } = error;
    if (response && response.status === 402) {
      const appRole = CURRENT_ENV.APP_ROLE;
      let refreshToken = localStorage.getItem('refreshToken') ?? '';
      if (!refreshToken) {
        Auth.clearToken();
        Auth.clearRefreshToken();
        window.location.href = '/';
      }

      if (!isRefreshing) {
        isRefreshing = true;
        if (
          response?.data?.module &&
          response?.data?.module !== '/api/v1/admin/refresh' &&
          !!refreshToken
        ) {
          let as = axios.create({ baseURL: CURRENT_ENV.API_URL });
          let url = '';
          switch (appRole) {
            case 'busk-admin':
              url = '/api/v1/admin/refresh';
              break;
            case 'warehouse-admin':
              url = '/api/v1/warehouse/refresh';
              break;
            default:
              url = '/api/v1/seller/refresh';
              break;
          }
          as.get(url, {
            headers: {
              authorization: refreshToken,
              'Accept-Language': 'kr',
              'Accept-Content': 'application/json',
              Accept: '*/*'
            }
          })
            .then((res) => {
              if (!!res?.data?.data.accessToken) {
                localStorage.setItem('token', res.data.data.accessToken);
                isRefreshing = false;
                onRefreshed(res.data.data.accessToken);
                subscribers = [];
                if (error.config.method == 'get') {
                  window.location.reload();
                }
              } else {
                Auth.clearToken();
                Auth.clearRefreshToken();
                window.location.href = '/';
              }
            })
            .catch((e) => {
              Auth.clearToken();
              Auth.clearRefreshToken();
              window.location.href = '/';
            });
        }
      }
      if (error.config.method !== 'get') {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            resolve(
              authAxios(error.config).then(({ data }: AxiosResponse) => data)
            );
          });
        });
      }
    }
    if (response && [401, 403].includes(response.status)) {
      Auth.clearToken();
      Auth.clearRefreshToken();
      window.location.href = '/';
      // message.error(response.data.message);
    }
    return Promise.reject(error);
  }
);

type CallWithParamsMethod = (
  method: 'get' | 'delete',
  url: string,
  body?: Record<string, any>,
  options?: Record<string, any>,
  useAuth?: boolean,
  usePrefix?: boolean
) => Promise<any>;
type CallWithBodyMethod = (
  method: 'post' | 'put' | 'patch',
  url: string,
  body?: Record<string, any>,
  options?: Record<string, any>,
  useAuth?: boolean,
  usePrefix?: boolean
) => Promise<any>;
type VerbMethod = (
  url: string,
  body?: Record<string, any>,
  options?: Record<string, any>,
  useAuth?: boolean,
  usePrefix?: boolean
) => Promise<any>;

class BaseService {
  public constructor(private prefix: string, private useAuth = true) {}

  public callWithParams: CallWithParamsMethod = (
    method,
    url,
    params,
    options,
    useAuth = true,
    usePrefix = true
  ) => {
    if (useAuth && this.useAuth) {
      return authAxios[method](usePrefix ? `${this.prefix}${url}` : url, {
        params,
        ...options
      })
        .then(({ data }: AxiosResponse) => data)
        .catch((e) => e);
    }

    return axiosBase[method](usePrefix ? `${this.prefix}${url}` : url, {
      params,
      ...options
    }).then(({ data }: AxiosResponse) => data);
  };

  public callWithBody: CallWithBodyMethod = (
    method,
    url,
    body,
    options,
    useAuth = true,
    usePrefix = true
  ) => {
    if (useAuth && this.useAuth) {
      return authAxios[method](
        usePrefix ? `${this.prefix}${url}` : url,
        body,
        options
      ).then(({ data }: AxiosResponse) => data);
    }

    return axiosBase[method](
      usePrefix ? `${this.prefix}${url}` : url,
      body,
      options
    ).then(({ data }: AxiosResponse) => data);
  };

  public initEventSource = (url: string) => {
    return new EventSourcePolyfill(`${CURRENT_ENV.API_URL}${url}`, {
      headers: {
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
        'X-Accel-Buffering': 'no',
        Authorization: localStorage.getItem('token')!
      }
    });
  };

  public assigneQueryParams = (query: IQueryParams) => {
    for (const key in query.filter) {
      if (key && key !== '') {
        Object.assign(query, {
          [`filter.${key}`]: query.filter[key as keyof IFilter]
        });
      }
    }
    delete query.filter;

    return new URLSearchParams(query as Record<string, string>).toString();
  };

  public get: VerbMethod = (
    url,
    params,
    options,
    useAuth,
    usePrefix = true
  ) => {
    return this.callWithParams('get', url, params, options, useAuth, usePrefix);
  };

  public post: VerbMethod = (url, body, options, useAuth, usePrefix = true) => {
    return this.callWithBody('post', url, body, options, useAuth, usePrefix);
  };

  public put: VerbMethod = (url, body, options, useAuth, usePrefix = true) => {
    return this.callWithBody('put', url, body, options, useAuth, usePrefix);
  };

  public patch: VerbMethod = (
    url,
    body,
    options,
    useAuth,
    usePrefix = true
  ) => {
    return this.callWithBody('patch', url, body, options, useAuth, usePrefix);
  };

  public delete: VerbMethod = (
    url,
    params,
    options,
    useAuth,
    usePrefix = true
  ) => {
    return this.callWithParams(
      'delete',
      url,
      params,
      options,
      useAuth,
      usePrefix
    );
  };

  public acceptImage = async (file: RcFile, urlPresigned: string) => {
    try {
      var config: Object = {
        method: 'put',
        url: urlPresigned,
        headers: {
          'Content-Type': file.type
        },
        data: file
      };
      const res = await axios(config);
      return res;
    } catch (e) {
      console.log(e);
    }
  };

  public uploadImage = async (body: Record<string, any>) => {
    try {
      const resUrlPresigned = await axiosBase.post('/upload-file/', body);
      return resUrlPresigned;
    } catch (e) {
      return Promise.reject(e);
    }
  };
}

export default BaseService;
